import { Component, OnInit } from '@angular/core';
import { ChromadbService } from '../../services/chromadb.service';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

export interface Collections {
  name: string;
  id?: string;
  dropdown: boolean;
  info: boolean;
  embedding_function: string;
  distance: string;
  elements: number;
  created_at?: string;
  updated?: string;
  metadata?: Record<string, any>;
}

export interface MetadataEntry {
  key: string;
  value: string;
}

export interface editSystemInfo {
  distance?: string;
  embedding_function?: string;
  created_at?: string;
  updated?: string;
}

export interface modal {
  title: string;
  collection: Collections;
  visible: boolean;
}

export interface edit extends modal {
  newName: string;
  metadata: MetadataEntry[];
  systemInfo: editSystemInfo;
}

export interface search extends modal {
  query: string;
  limit: number;
}

export interface create extends modal {
  name: string;
  distance: string;
  embedding_function: string;
}

interface banner {
  title: string;
  text: string;
  color: boolean;
  bool: boolean;  
}

@Component({
  selector: 'app-collections',
  standalone: true,
  imports: [CommonModule, NgFor, NgIf, FormsModule],
  templateUrl: './collections.component.html',
  styleUrl: './collections.component.css'
})
export class CollectionsComponent implements OnInit {

  public collectionCount: number | null = null;
  public hasMoreCollections: boolean = false;
  public isLoading: boolean = false;
  showWarningModal: boolean = false;

  constructor(private chromadb: ChromadbService, private router: Router) {}

  allCollections: Collections[] = [];

  // Modelos del formulario de filtros
  searchTerm: string = '';
  collectionLimit: number | null = 50;
  filterStartDate: string = '';
  filterEndDate: string = '';

  // Filtros aplicados efectivamente en la lista
  appliedSearchTerm: string = '';
  appliedCollectionLimit: number | null = 50;
  appliedFilterStartDate: string = '';
  appliedFilterEndDate: string = '';

  currentPage: number = 1;

  get collections(): Collections[] {
    return this.displayedCollections;
  }

  get displayedCollections(): Collections[] {
    return this.allCollections;
  }

  get filteredCollections(): Collections[] {
    return this.allCollections;
  }

  get limitedCollections(): Collections[] {
    return this.allCollections;
  }

  get isPaginated(): boolean {
    return (this.appliedCollectionLimit !== null && this.appliedCollectionLimit !== undefined && this.appliedCollectionLimit > 0) &&
           (this.currentPage > 1 || this.hasMoreCollections);
  }

  get targetTopicsCollections(): Collections[] {
    return this.allCollections;
  }

  get hasActiveTopicFilter(): boolean {
    const hasLimit = this.appliedCollectionLimit !== null && this.appliedCollectionLimit !== undefined && this.appliedCollectionLimit > 0;
    const hasSearch = !!(this.appliedSearchTerm && this.appliedSearchTerm.trim());
    const hasDate = !!(this.appliedFilterStartDate || this.appliedFilterEndDate);
    return hasLimit || hasSearch || hasDate;
  }

  delete_modal: modal = {
    title: "",
    collection: { name: "", dropdown: false, info: false, distance: "", embedding_function: "", elements: 0 },
    visible: false
  };
  edit_modal: edit = {
    title: "",
    collection: { name: "", dropdown: false, info: false, distance: "", embedding_function: "", elements: 0 },
    visible: false,
    newName: "",
    metadata: [],
    systemInfo: {}
  };
  search_modal: search = {
    title: "",
    collection: { name: "", dropdown: false, info: false, distance: "", embedding_function: "", elements: 0 },
    visible: false,
    query: "",
    limit: 10
  };
  banner: banner = {  
    title: "",
    text: "",
    color: false,
    bool: false
  };
  create_modal: boolean = false;
  selected_distance: any;
  selected_function: any;
  newCollection_name: string = "";
  newCollection_metadata: MetadataEntry[] = [];
  distance_models = [
    { name: "Cosine", value: "cosine" },
    { name: "Euclidean", value: "euclidean" },
  ];
  embedding_functions = [
    { name: "Default", value: "default" },
    { name: "Embedding-002", value: "text-embedding-ada-002" },
  ];

  public init(): void {
    this.selected_distance = this.distance_models[0].value;
    this.selected_function = this.embedding_functions[0].value;
    this.appliedSearchTerm = this.searchTerm;
    const numLimit = (this.collectionLimit !== null && this.collectionLimit !== undefined && (this.collectionLimit as any) !== '')
      ? Number(this.collectionLimit)
      : null;
    this.appliedCollectionLimit = (numLimit && numLimit > 0) ? numLimit : null;
    this.appliedFilterStartDate = this.filterStartDate;
    this.appliedFilterEndDate = this.filterEndDate;
    this.loadCollections(1);
  }

  ngOnInit(): void {
    this.init();
  }

  loadCollections(page: number = 1): void {
    this.currentPage = page;
    this.isLoading = true;
    const limit = (this.appliedCollectionLimit !== null && this.appliedCollectionLimit !== undefined && this.appliedCollectionLimit > 0)
      ? Number(this.appliedCollectionLimit)
      : null;
    const offset = limit ? (page - 1) * limit : 0;

    this.chromadb.getCollections({
      limit,
      offset,
      search: this.appliedSearchTerm || null,
      start_date: this.appliedFilterStartDate || null,
      end_date: this.appliedFilterEndDate || null
    }).subscribe({
      next: (data: any) => {
        let collections: Collections[] = [];
        if (Array.isArray(data)) {
          for (const item of data) {
            collections.push({
              name: item.name,
              id: item.id,
              dropdown: false,
              info: false,
              embedding_function: item.function,
              distance: item.distance,
              elements: Number(item.count) || 0,
              created_at: item.created_at,
              updated: item.updated,
              metadata: item.metadata || {}
            });
          }
        }
        const rawCount = collections.length;
        if (limit && collections.length > limit) {
          collections = collections.slice(0, limit);
        }
        this.allCollections = collections;
        if (limit) {
          this.hasMoreCollections = rawCount >= limit;
          this.collectionCount = null;
        } else {
          this.hasMoreCollections = false;
          this.collectionCount = collections.length;
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error cargando colecciones:', err);
        this.isLoading = false;
        this.openBanner('Error', 'No se pudieron cargar las colecciones', false);
      }
    });
  }

  sortCollections(): void {
    this.allCollections.sort((a, b) => {
      if (a.updated && b.updated) {
        return new Date(b.updated).getTime() - new Date(a.updated).getTime();
      }
      if (a.updated && !b.updated) return -1;
      if (!a.updated && b.updated) return 1;
      return 0;
    });
  }

  applyFilters(): void {
    this.appliedSearchTerm = this.searchTerm;
    const numLimit = (this.collectionLimit !== null && this.collectionLimit !== undefined && (this.collectionLimit as any) !== '')
      ? Number(this.collectionLimit)
      : null;
    this.appliedCollectionLimit = (numLimit && numLimit > 0) ? numLimit : null;
    this.appliedFilterStartDate = this.filterStartDate;
    this.appliedFilterEndDate = this.filterEndDate;
    this.loadCollections(1);
  }

  onFilterChange(): void {
    this.currentPage = 1;
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
  }

  clearAllFilters(): void {
    this.searchTerm = '';
    this.collectionLimit = 50;
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.applyFilters();
  }

  prevPage(): void {
    if (this.currentPage > 1 && !this.isLoading) {
      this.loadCollections(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.hasMoreCollections && !this.isLoading) {
      this.loadCollections(this.currentPage + 1);
    }
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateStr;
    }
  }

  getCustomMetadata(collection: Collections): MetadataEntry[] {
    const rawMeta = collection.metadata || {};
    const protectedKeys = ['hnsw:space', 'embedding_function', 'created_at', 'updated', 'document_count'];
    const entries: MetadataEntry[] = [];
    for (const [k, v] of Object.entries(rawMeta)) {
      if (!protectedKeys.includes(k)) {
        entries.push({ key: k, value: v !== null && v !== undefined ? String(v) : '' });
      }
    }
    return entries;
  }

  addCreateMetadata(): void {
    this.newCollection_metadata.push({ key: '', value: '' });
  }

  removeCreateMetadata(index: number): void {
    this.newCollection_metadata.splice(index, 1);
  }

  addEditMetadata(): void {
    this.edit_modal.metadata.push({ key: '', value: '' });
  }

  removeEditMetadata(index: number): void {
    this.edit_modal.metadata.splice(index, 1);
  }

  openCreateModal(): void {
    this.newCollection_name = '';
    this.newCollection_metadata = [];
    this.selected_distance = this.distance_models[0].value;
    this.selected_function = this.embedding_functions[0].value;
    this.create_modal = true;
  }

  public syncCollectionCount(collection: Collections): void {
    collection.dropdown = false;
    this.chromadb.syncCollectionCount(collection.name).subscribe({
      next: (res: any) => {
        if (res && res.status === 'success') {
          collection.elements = res.count;
          if (res.updated) {
            collection.updated = res.updated;
            if (!collection.metadata) {
              collection.metadata = {};
            }
            collection.metadata['document_count'] = res.count;
            collection.metadata['updated'] = res.updated;
            this.sortCollections();
          }
          this.openBanner(
            'Conteo actualizado',
            `Colección '${collection.name}': ${res.count} ${res.count === 1 ? 'documento' : 'documentos'}.`,
            true
          );
        }
      },
      error: (err: any) => {
        console.error('Error sincronizando conteo:', err);
        this.openBanner(
          'Error al contar documentos',
          `No se pudo sincronizar el conteo de '${collection.name}'.`,
          false
        );
      }
    });
  }

  public openModal(collection: Collections, type: string): void {
    collection.dropdown = false;    
    if (type == "delete") {
      this.delete_modal.collection = collection;
      this.delete_modal.title = "¿Quieres eliminar la colección " + collection.name + "?";
      this.delete_modal.visible = true;
    }
    if (type == "edit") {
      this.edit_modal.collection = collection;
      this.edit_modal.newName = collection.name;
      this.edit_modal.title = "Modificación de la colección " + collection.name;
      this.edit_modal.systemInfo = {
        distance: collection.distance,
        embedding_function: collection.embedding_function,
        created_at: collection.created_at,
        updated: collection.updated
      };

      const rawMeta = collection.metadata || {};
      const protectedKeys = ['hnsw:space', 'embedding_function', 'created_at', 'updated', 'document_count'];
      const customEntries: MetadataEntry[] = [];
      for (const [k, v] of Object.entries(rawMeta)) {
        if (!protectedKeys.includes(k)) {
          customEntries.push({ key: k, value: v !== null && v !== undefined ? String(v) : '' });
        }
      }
      this.edit_modal.metadata = customEntries;
      this.edit_modal.visible = true;
    }
    if (type == "search") {
      this.search_modal.collection = collection;
      this.search_modal.title = "Buscar en la colección " + collection.name;
      this.search_modal.visible = true;
    }
  }

  public openDropdown(collection: Collections): void {
    collection.dropdown = !collection.dropdown;
    for (let c of this.allCollections) {
      if (c.name !== collection.name) {
        c.dropdown = false;
      }
    }
  }

  public openInfo(collection: Collections): void {
    collection.info = !collection.info;
    for (let c of this.allCollections) {
      if (c.name !== collection.name) {
        c.info = false;
      }
    }
  }

  //Va a la ruta de la colección seleccionada
  public openCollection(name: string): void {
    this.router.navigate([`/collection/${name}`]);
  }

  public deleteCollection(collection: Collections): void {
    console.log("delete");
    this.chromadb.deleteCollection(collection.name).subscribe((data: any) => {
      collection.dropdown = false;
      this.delete_modal.visible = false;
      this.init();
      this.openBanner("Colección eliminada", "La colección " + collection.name + " ha sido eliminada", true);
    });
  }

  public editCollection(collection: Collections, newName: string): void {
    console.log("edit");
    const metaObj: Record<string, any> = {};
    for (const item of this.edit_modal.metadata) {
      const k = item.key ? item.key.trim() : '';
      if (k) {
        metaObj[k] = item.value;
      }
    }

    this.chromadb.editCollectionName(collection.name, newName, metaObj).subscribe({
      next: (data: any) => {
        collection.dropdown = false;
        this.edit_modal.visible = false;
        this.edit_modal.newName = "";
        this.edit_modal.metadata = [];
        this.init();
      },
      error: (error: any) => {
        console.log(error);
      }
    });
  }

  public createCollection(): void {
    console.log("create");
    const metaObj: Record<string, any> = {};
    for (const item of this.newCollection_metadata) {
      const k = item.key ? item.key.trim() : '';
      if (k) {
        metaObj[k] = item.value;
      }
    }

    const hasCustomMeta = Object.keys(metaObj).length > 0;
    const createObs = hasCustomMeta
      ? this.chromadb.createCollection(this.newCollection_name, this.selected_function, this.selected_distance, metaObj)
      : this.chromadb.createCollection(this.newCollection_name, this.selected_function, this.selected_distance);

    createObs.subscribe({
      next: (data: any) => {
        this.create_modal = false;
        this.newCollection_name = "";
        this.newCollection_metadata = [];
        this.openBanner("Colección creada", "La colección se ha creado correctamente", true);
        this.init();
      },
      error: (error: any) => {
        console.log(error.error);
        if (error.error && error.error.message == "Collection already exists") {
          this.openBanner("Error", "La colección ya existe", false);
        } else {
          this.openBanner("Error", "Error al crear la colección", false);
        }
      }
    });
  }

  public searchCollection(collection: Collections, query: string, limit: number): void {
    console.log("Navegando a la búsqueda...");
    const name = collection.name;
    this.router.navigate(['/search', name], {
      queryParams: {
        query: query,
        limit: limit
      }
    });
  }

  openBanner(title: string, description: string, color: boolean): void {
    this.banner.title = title;
    this.banner.text = description;
    this.banner.color = color;
    this.banner.bool = true;
    setTimeout(() => {
      this.banner.bool = false;
    }, 5000);
  }

  closeBanner(): void {
    this.banner.bool = false;
  }

  openTopicsForCollection(collection: Collections): void {
    collection.dropdown = false;
    const queryParams: any = {};
    const sDate = this.appliedFilterStartDate || this.filterStartDate;
    if (sDate) {
      queryParams.startDate = sDate;
    }
    const eDate = this.appliedFilterEndDate || this.filterEndDate;
    if (eDate) {
      queryParams.endDate = eDate;
    }
    this.router.navigate(['/topics', collection.name], { queryParams });
  }

  onTopicsClick(): void {
    this.showWarningModal = true;
  }

  cancelNavigation(): void {
    this.showWarningModal = false;
  }

  confirmNavigation(): void {
    this.showWarningModal = false;
    const queryParams: any = {};
    if (this.hasActiveTopicFilter) {
      const names = this.targetTopicsCollections.map(c => c.name);
      if (names.length > 0) {
        queryParams.collections = names.join(',');
      }
    }
    const sDate = this.appliedFilterStartDate || this.filterStartDate;
    if (sDate) {
      queryParams.startDate = sDate;
    }
    const eDate = this.appliedFilterEndDate || this.filterEndDate;
    if (eDate) {
      queryParams.endDate = eDate;
    }
    this.router.navigate(['/topics'], { queryParams });
  }  
}
